import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ImplementationStep } from '../types';

const QuickStartWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const implementationSteps: ImplementationStep[] = [
    {
      id: '1',
      title: 'System Requirements Check',
      description: 'Verify your infrastructure meets minimum requirements for camlytix AI deployment',
      status: 'completed',
      estimatedTime: '15 minutes',
      resources: ['Hardware Specifications', 'Network Requirements', 'Software Dependencies']
    },
    {
      id: '2',
      title: 'Initial Configuration',
      description: 'Set up your camlytix AI instance with basic configuration parameters',
      status: 'in-progress',
      estimatedTime: '30 minutes',
      resources: ['Configuration Guide', 'Environment Setup', 'Security Settings']
    },
    {
      id: '3',
      title: 'Camera Integration',
      description: 'Connect your existing surveillance infrastructure to the analytics platform',
      status: 'pending',
      estimatedTime: '45 minutes',
      resources: ['Camera Compatibility List', 'RTSP Configuration', 'Stream Optimization']
    },
    {
      id: '4',
      title: 'Analytics Calibration',
      description: 'Fine-tune AI models for your specific environment and use cases',
      status: 'pending',
      estimatedTime: '1 hour',
      resources: ['Calibration Wizard', 'Accuracy Testing', 'Model Selection']
    },
    {
      id: '5',
      title: 'Dashboard Setup',
      description: 'Configure your analytics dashboard and reporting preferences',
      status: 'pending',
      estimatedTime: '30 minutes',
      resources: ['Dashboard Templates', 'Widget Configuration', 'Report Scheduling']
    },
    {
      id: '6',
      title: 'User Training & Go-Live',
      description: 'Complete team training and activate your production environment',
      status: 'pending',
      estimatedTime: '2 hours',
      resources: ['Training Materials', 'User Guides', 'Go-Live Checklist']
    }
  ];

  const getStatusIcon = (status: ImplementationStep['status']) => {
    switch (status) {
      case 'completed':
        return <Icon name="CheckCircle2" size={24} className="text-success" />;
      case 'in-progress':
        return <Icon name="Clock" size={24} className="text-warning" />;
      default:
        return <Icon name="Circle" size={24} className="text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: ImplementationStep['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 border-success/30';
      case 'in-progress':
        return 'bg-warning/10 border-warning/30';
      default:
        return 'bg-muted border-border';
    }
  };

  return (
    <section id="quick-start" className="py-16 px-4 md:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Implementation Wizard
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Follow our step-by-step guide to deploy camlytix AI with confidence. Track your progress and access relevant resources at each stage.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-2xl border border-border p-8">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-text-secondary">
                    Step {currentStep + 1} of {implementationSteps.length}
                  </span>
                  <span className="text-sm font-medium text-primary">
                    {Math.round(((currentStep + 1) / implementationSteps.length) * 100)}% Complete
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-500"
                    style={{ width: `${((currentStep + 1) / implementationSteps.length) * 100}%` }}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  {getStatusIcon(implementationSteps[currentStep].status)}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {implementationSteps[currentStep].title}
                    </h3>
                    <p className="text-text-secondary mb-4">
                      {implementationSteps[currentStep].description}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-text-secondary">
                      <div className="flex items-center space-x-2">
                        <Icon name="Clock" size={16} />
                        <span>{implementationSteps[currentStep].estimatedTime}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="FileText" size={16} />
                        <span>{implementationSteps[currentStep].resources.length} Resources</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-xl p-6">
                  <h4 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="BookOpen" size={20} className="mr-2" />
                    Available Resources
                  </h4>
                  <div className="space-y-3">
                    {implementationSteps[currentStep].resources.map((resource, index) => (
                      <button
                        key={index}
                        className="w-full flex items-center justify-between p-3 bg-background rounded-lg hover:bg-primary/5 transition-colors duration-300 group"
                        onClick={() => console.log(`Opening resource: ${resource}`)}
                      >
                        <span className="text-sm font-medium text-foreground">{resource}</span>
                        <Icon name="ExternalLink" size={16} className="text-text-secondary group-hover:text-primary transition-colors duration-300" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-border">
                  <Button
                    variant="outline"
                    size="default"
                    iconName="ChevronLeft"
                    iconPosition="left"
                    disabled={currentStep === 0}
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                  >
                    Previous Step
                  </Button>
                  <Button
                    variant="default"
                    size="default"
                    iconName="ChevronRight"
                    iconPosition="right"
                    disabled={currentStep === implementationSteps.length - 1}
                    onClick={() => setCurrentStep(Math.min(implementationSteps.length - 1, currentStep + 1))}
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-bold text-foreground mb-4 flex items-center">
                <Icon name="List" size={20} className="mr-2" />
                All Steps
              </h3>
              <div className="space-y-3">
                {implementationSteps.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(index)}
                    className={`w-full text-left p-4 rounded-xl border transition-all duration-300 ${
                      currentStep === index
                        ? 'border-primary bg-primary/5'
                        : getStatusColor(step.status)
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {getStatusIcon(step.status)}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground mb-1 truncate">
                          {step.title}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {step.estimatedTime}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-2xl border border-accent/20 p-6">
              <div className="flex items-start space-x-3 mb-4">
                <Icon name="Lightbulb" size={24} className="text-accent flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Need Help?</h4>
                  <p className="text-sm text-text-secondary mb-4">
                    Our implementation specialists are available 24/7 to assist you.
                  </p>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="MessageCircle"
                    iconPosition="left"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground"
                    onClick={() => console.log('Opening support chat')}
                  >
                    Chat with Expert
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickStartWizard;